import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./MicSetupModal.scss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { PermissionStatus } from "../../utils/media-devices-utils";
import {
  Subtitle1,
  makeStyles,
  Button,
  Select,
  Caption1,
  DialogActions,
  MenuItem,
  ProgressBar,
  Field,
  OverlayDrawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle
} from "@fluentui/react-components";
import { ArrowLeftRegular, MicFilled, Speaker220Filled, VideoFilled, ArrowExitFilled } from "@fluentui/react-icons";
import { MicPopoverSelect } from "../fluent/MicPopoverSelect";
import configManager from "../../singleton/ConfigManager";
import useBreakpoints from "../hooks/useBreakpoints";

export const tittleMessages = defineMessages({
  microphoneSetup: {
    id: "mic-setup-modal.title",
    defaultMessage: "Mic Setup"
  },
  audioSetup: {
    id: "mic-setup-modal.title-audio",
    defaultMessage: "Audio & Video"
  }
});

const useStyles = makeStyles({
  entryCard: {
    display: "flex",
    width: "320px",
    minWidth: "320px",
    maxWidth: "479px",
    padding: "var(--Horizontal-XXL, 24px)",
    flexDirection: "column",
    alignItems: "flex-start",
    bottom: "25px",
    right: "30px",
    margin: "unset",
    marginLeft: "auto",
    marginTop: "auto"
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  titleContainer: {
    display: "flex",
    gap: "8px"
  },
  audioText: {
    display: " flex",
    padding: " 8px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: " var(--Font-size-100, 10px)",
    color: "#828282"
  },
  audioContainerInner: {
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  },
  devicesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  acceptButton: {
    marginTop: "10px"
  },
  micProgressContainer: {
    pointerEvents: "none",
    alignItems: "center",
    "&:hover": {},
    "&:hover > * ": {
      color: "inherit!important"
    }
  },
  progressBarColor: {
    "& .fui-ProgressBar__bar": {
      backgroundColor: "var(--Green-1, #219653)"
    }
  },
  menuListItem: {
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    },
    "&:hover > * ": {
      color: "inherit!important"
    }
  },
  deniedText: {
    color: "var(--White, #FFF)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal"
  },
  deniedTextBody: {
    color: "var(--Gray-3, #828282)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "normal"
  }
});

export function MicSetupModal({
  className,
  microphoneOptions,
  onChangeMicrophone,
  speakerOptions,
  onChangeSpeaker,
  isMicrophoneEnabled,
  onPlaySound,
  isMicrophoneMuted,
  onChangeMicrophoneMuted,
  onEnterRoom,
  onBack,
  permissionStatus,
  isAudioInputSelectAvailable,
  isAudioOutputSelectAvailable,
  micLevelBar,
  speakerLevelBar,
  canVoiceChat,
  enteredInAr,
  videoOptions,
  onChangeVideo,
  requestingVideoPermission,
  micChanges,
  permissionStatusVideo,
  fetchVideoDevices,
  cameraNotConfigured,
  ...rest
}) {
  const intl = useIntl();
  const classes = useStyles();
  const showAudio = configManager.config.voiceChat?.enabled;
  const showVideo = configManager.config.spawnCamera?.enabled;
  const showSpeaker = configManager.config.deviceDialog?.showSpeaker;
  const breakpoint = useBreakpoints();

  const [open, setIsOpen] = useState(true);

  const callbackOpen = async cb => {
    setIsOpen(false);
    setTimeout(() => {
      cb();
    }, 100);
  };

  const handleClick = () => {
    if (configManager.config.spawnCamera?.enabled && cameraNotConfigured) {
      fetchVideoDevices();
    }
  };

  return (
    <OverlayDrawer
      position={["xs", "sm", "md"].includes(breakpoint) ? "bottom" : "end"}
      open={open}
      modalType="non-modal"
      className={styles.entryCard}
      id="mic-setup-modal"
    >
      <DrawerHeader>
        <DrawerHeaderTitle className={classes.titleContainer}>
          <Button
            onClick={() => callbackOpen(onBack)}
            style={{ height: "30px", width: "30px", minWidth: "30px", marginRight: "10px" }}
            icon={<ArrowLeftRegular style={{ width: "18px" }} />}
          ></Button>
          <Subtitle1>Audio & Video</Subtitle1>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={classes.dialogContent} style={{ width: "100%" }}>
        <div className={classes.devicesContainer}>
          {showAudio && (
            <div className="audio--container" id="audio-setup-container">
              <Caption1 className={classes.audioText}>Audio </Caption1>

              <div className={classes.audioContainerInner}>
                {showAudio && (
                  <div>
                    {(canVoiceChat && (
                      <>
                        {permissionStatus === PermissionStatus.GRANTED && isAudioInputSelectAvailable && (
                          <MicPopoverSelect
                            positioning="above"
                            name={"mic"}
                            Icon={MicFilled}
                            {...microphoneOptions}
                            onChange={onChangeMicrophone}
                          >
                            <MenuItem className={classes.micProgressContainer} icon={<MicFilled />}>
                              <ProgressBar
                                className={classes.progressBarColor}
                                value={micChanges}
                                thickness="large"
                                color="#219653"
                              />
                            </MenuItem>
                          </MicPopoverSelect>
                        )}
                        <div>
                          {permissionStatus === PermissionStatus.GRANTED ? (
                            <></>
                          ) : (
                            (permissionStatus === PermissionStatus.PROMPT && (
                              <Field label={"Requesting access to your microphone..."} validationState="none">
                                <ProgressBar thickness="large" />
                              </Field>
                            )) ||
                            (permissionStatus === PermissionStatus.DENIED && (
                              <div
                                style={{ display: "flex", alignItems: "start", flexDirection: "column", gap: "3px" }}
                              >
                                <p className={classes.deniedText}>
                                  <FormattedMessage
                                    id="mic-setup-modal.error-title"
                                    defaultMessage="Microphone access was blocked."
                                    className={classes.deniedText}
                                  />
                                </p>{" "}
                                <span className={classes.deniedTextBody}>
                                  <FormattedMessage
                                    id="mic-setup-modal.error-description"
                                    defaultMessage="To use the voice chat feature, you will need to grant access for your microphone."
                                  />
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )) || (
                      <div>
                        <p>
                          <FormattedMessage
                            id="mic-setup-modal.voice-chat-disabled"
                            defaultMessage="Voice chat is <bold>turned off</bold> for this room."
                            values={{
                              bold: str => <b>{str}</b>
                            }}
                          />
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {showSpeaker && (
                  <div>
                    {isAudioOutputSelectAvailable &&
                      speakerOptions?.options &&
                      !(speakerOptions.options[0].value == "none" && speakerOptions.options.length === 1) && (
                        <div>
                          <div>
                            <div>
                              <MicPopoverSelect
                                positioning="above"
                                name={"speaker"}
                                Icon={Speaker220Filled}
                                {...speakerOptions}
                                onChange={onChangeSpeaker}
                              >
                                <MenuItem
                                  persistOnClick={true}
                                  className={classes.menuListItem}
                                  onClick={onPlaySound}
                                  icon={<Speaker220Filled />}
                                >
                                  Test Speaker
                                </MenuItem>
                              </MicPopoverSelect>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          )}
          {showVideo && (
            <div className="video--container">
              {permissionStatusVideo !== PermissionStatus.DENIED && (
                <Caption1 className={classes.audioText}>Video </Caption1>
              )}
              {requestingVideoPermission && (
                <Field label={"Requesting access to your camera..."} validationState="none">
                  <ProgressBar thickness="large" />
                </Field>
              )}
              {!requestingVideoPermission && permissionStatusVideo !== PermissionStatus.DENIED && (
                <div className={classes.audioContainerInner}>
                  <MicPopoverSelect
                    positioning="above"
                    name={"video"}
                    Icon={VideoFilled}
                    {...videoOptions}
                    onChange={onChangeVideo}
                    onClick={handleClick}
                  ></MicPopoverSelect>
                </div>
              )}
              {permissionStatusVideo === PermissionStatus.DENIED && (
                <div style={{ display: "flex", alignItems: "start", flexDirection: "column", gap: "3px" }}>
                  <p className={classes.deniedText}>Camera access has been blocked</p>{" "}
                  <span className={classes.deniedTextBody}>
                    To use the video chat features, you will need to grant access to your camera.
                  </span>
                </div>
              )}
            </div>
          )}
          <Button 
            appearance="primary" 
            className={classes.acceptButton} 
            onClick={() => callbackOpen(onEnterRoom)}
            id="enter-room-button"
          >
            <FormattedMessage id="mic-setup-modal.enter-room-button" defaultMessage="Enter" />
          </Button>
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
}

MicSetupModal.propTypes = {
  className: PropTypes.string,
  onPlaySound: PropTypes.func,
  micLevelBar: PropTypes.node,
  speakerLevelBar: PropTypes.node,
  isMicrophoneEnabled: PropTypes.bool,
  isMicrophoneMuted: PropTypes.bool,
  onChangeMicrophoneMuted: PropTypes.func,
  microphoneOptions: PropTypes.object,
  onChangeMicrophone: PropTypes.func,
  speakerOptions: PropTypes.object,
  onChangeSpeaker: PropTypes.func,
  onEnterRoom: PropTypes.func,
  onBack: PropTypes.func,
  permissionStatus: PropTypes.string,
  isAudioInputSelectAvailable: PropTypes.bool,
  isAudioOutputSelectAvailable: PropTypes.bool,
  canVoiceChat: PropTypes.bool
};

MicSetupModal.defaultProps = {
  permissionStatus: PermissionStatus.PROMPT
};
